import React from 'react';
import { connect } from 'react-redux';
import { Col, Row } from 'antd';
import { motion } from 'framer-motion';
import { css, StyleSheet } from 'aphrodite';
import { Box } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

import BasePage from '../base';
import LoginInput from '../../common/login-input';
import TextBase from '../../common/text';
import ButtonGradient from '../../common/button-gradient';
import InputError from '../../common/input-error/InputError.component';
import CustomSpinner from '../../common/CustomSpinner';
import PasswordRules, { isPasswordValid } from '../../common/password-rules';
import { Colors } from '../../themes/Colors';
import { SVGImageComponentReact } from '../../themes/Images';
import { animate } from '../../utils/defined';
import { ROUTER_NAME } from '../../router/routers';
import { MESSAGE_TYPE, messageManager, getValueFromObjectByKeys, translate } from '../../helper';
import { publicRequest } from '../../connection/ApiDefine';
import WithNavigate from '../../component/WithNavigate';
import '../reset-password/resetPassword.style.css';

const styles = StyleSheet.create({
  flex: {
    display: 'flex',
    justifyContent: 'space-between',
    marginLeft: 'var(--sp-xl)',
    marginTop: 'var(--sp-xl)'
  }
});

const withTokenParam = (WrappedComponent) => (props) => {
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get('token');
  return <WrappedComponent {...props} token={token} />;
};

class SetPassword extends BasePage {
  constructor(props) {
    super(props);
    this.state = {
      newPassword: '',
      confirmPassword: '',
      showPassword: false,
      showConfirmPassword: false,
      passwordFocused: false,
      submitting: false,
      validating: true,
      tokenValid: false,
      tokenError: '',
      email: '',
      errors: {}
    };
  }

  componentDidMount() {
    const { token } = this.props;
    if (!token) {
      this.setState({
        validating: false,
        tokenError: translate('invalidLinkNoToken')
      });
      return;
    }
    this.validateToken();
  }

  validateToken = async () => {
    const { token } = this.props;
    try {
      const response = await publicRequest.get(`users/set-password/validate/?token=${token}`);
      if (response.data?.valid) {
        this.setState({
          tokenValid: true,
          email: response.data.email || '',
          validating: false
        });
      } else {
        this.setState({
          tokenError: translate('linkInvalidOrUsed'),
          validating: false
        });
      }
    } catch (error) {
      this.setState({
        tokenError: translate('linkInvalidOrUsed'),
        validating: false
      });
    }
  };

  onChangeNewPassword = (value) => {
    this.setState({ newPassword: value });
  };

  onChangeConfirmPassword = (value) => {
    this.setState({ confirmPassword: value });
  };

  validateForm = () => {
    const { newPassword, confirmPassword } = this.state;
    const newErrors = {};

    if (!isPasswordValid(newPassword)) {
      newErrors.new_password = [translate('passwordNotMeetRequirements')];
    }

    if (newPassword !== confirmPassword) {
      newErrors.confirm_password = [translate('passwordsDoNotMatch')];
    }

    this.setState({ errors: newErrors });
    return Object.keys(newErrors).length === 0;
  };

  handleSubmit = async () => {
    const { submitting, newPassword, confirmPassword } = this.state;
    const { token, navigate } = this.props;

    if (submitting) return;

    if (!this.validateForm()) return;

    this.setState({ submitting: true, errors: {} });

    try {
      const response = await publicRequest.post('users/set-password/', {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });

      this.setState({
        newPassword: '',
        confirmPassword: '',
        errors: {}
      });

      messageManager({
        type: MESSAGE_TYPE.success,
        messageShow: getValueFromObjectByKeys(response, ['data', 'detail']) || translate('passwordSetSuccess')
      });

      navigate(ROUTER_NAME.login);
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData) {
        if (errorData.token) {
          this.setState({
            tokenValid: false,
            tokenError: Array.isArray(errorData.token) ? errorData.token[0] : errorData.token,
            submitting: false
          });
        } else {
          const fieldErrors = {};
          if (errorData.new_password) {
            fieldErrors.new_password = Array.isArray(errorData.new_password)
              ? errorData.new_password
              : [errorData.new_password];
          }
          if (errorData.confirm_password) {
            fieldErrors.confirm_password = Array.isArray(errorData.confirm_password)
              ? errorData.confirm_password
              : [errorData.confirm_password];
          }
          this.setState({ errors: fieldErrors, submitting: false });
        }
      } else {
        messageManager({
          type: MESSAGE_TYPE.error,
          messageShow: translate('errorOccurredTryAgain')
        });
        this.setState({ submitting: false });
      }
    }
  };

  renderContent() {
    const {
      newPassword,
      confirmPassword,
      showPassword,
      showConfirmPassword,
      passwordFocused,
      submitting,
      validating,
      tokenValid,
      tokenError,
      email,
      errors
    } = this.state;
    const { navigate } = this.props;

    const IconEyePassword = showPassword
      ? SVGImageComponentReact.IconEyeOffPassword
      : SVGImageComponentReact.IconEyePassword;

    const IconEyePasswordConfirm = showConfirmPassword
      ? SVGImageComponentReact.IconEyeOffPassword
      : SVGImageComponentReact.IconEyePassword;

    if (validating) {
      return (
        <motion.div
          initial={animate.initial}
          animate={animate.animate}
          transition={animate.transition}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
            <CustomSpinner />
          </div>
        </motion.div>
      );
    }

    if (!tokenValid) {
      return (
        <motion.div
          initial={animate.initial}
          animate={animate.animate}
          transition={animate.transition}
        >
          <div
            className="max-width-100"
            style={{
              paddingTop: 'calc(var(--fs-hero)*2.35849057)',
              paddingInline: 'var(--sp-lg)',
              paddingBottom: 'var(--sp-lg)',
              textAlign: 'center'
            }}
          >
            <TextBase
              style={{ marginBottom: 'var(--sp-md2)' }}
              fontWeight={600}
              size={'calc(var(--fs-xl)*1.50943)'}
              color={Colors.black}
            >
              setPassword
            </TextBase>
            <TextBase
              style={{ marginTop: 'var(--sp-lg)' }}
              size={'var(--fs-body)'}
              color={Colors.red}
            >
              {tokenError}
            </TextBase>
            <div
              style={{ cursor: 'pointer', marginTop: 'var(--sp-lg)' }}
              onClick={() => navigate(ROUTER_NAME.login)}
            >
              <TextBase size={'var(--fs-body)'} color={Colors.main}>
                goToLogin
              </TextBase>
            </div>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div
        initial={animate.initial}
        animate={animate.animate}
        transition={animate.transition}
      >
        <div className={css(styles.flex)}>
          <div></div>
        </div>
        <div
          className="max-width-100"
          style={{
            paddingTop: 'calc(var(--fs-hero)*2.35849057)',
            paddingInline: 'var(--sp-lg)',
            paddingBottom: 'var(--sp-lg)'
          }}
        >
          <TextBase
            style={{ marginBottom: 'var(--sp-md2)' }}
            fontWeight={600}
            size={'calc(var(--fs-xl)*1.50943)'}
            color={Colors.black}
          >
            setPassword
          </TextBase>

          {email && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                backgroundColor: '#F5F5F5',
                borderRadius: '8px',
                marginBottom: 'var(--sp-lg)',
                border: '1px solid #E0E0E0'
              }}
            >
              <EmailOutlinedIcon sx={{ color: '#757575', fontSize: 20 }} />
              <div>
                <TextBase
                  size={'12px'}
                  color={Colors.grey2}
                  style={{ marginBottom: '2px' }}
                >
                  settingPasswordFor
                </TextBase>
                <TextBase
                  size={'14px'}
                  fontWeight={600}
                  color={Colors.black}
                >
                  {email}
                </TextBase>
              </div>
            </Box>
          )}

          <div className="form-wrapper">
            <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} style={{ marginTop: 'calc(var(--sp-lg)*1.886792)' }}>
              <Col span={24} className="row-margin">
                <div>
                  <TextBase
                    style={{ textAlign: 'left' }}
                    size={'calc(var(--fs-xs)*1.50943)'}
                    color={Colors.black}
                  >
                    password
                  </TextBase>
                  <LoginInput
                    value={newPassword}
                    onChange={this.onChangeNewPassword}
                    onFocus={() => this.setState({ passwordFocused: true })}
                    placeholder="enterPassword"
                    type={showPassword ? 'text' : 'password'}
                    rightElement={
                      <IconEyePassword
                        className="cursor-pointer"
                        onClick={() => {
                          this.setState({ showPassword: !showPassword });
                        }}
                      />
                    }
                  />
                  <InputError error={errors.new_password} />
                  <PasswordRules password={newPassword} isFocused={passwordFocused} />
                </div>
              </Col>

              <Col span={24} className="row-margin">
                <div className="max-width-100">
                  <TextBase
                    className="no-select"
                    style={{ textAlign: 'left' }}
                    size={'calc(var(--fs-xs)*1.50943)'}
                    color={Colors.black}
                  >
                    confirmPassword
                  </TextBase>
                  <LoginInput
                    value={confirmPassword}
                    onChange={this.onChangeConfirmPassword}
                    placeholder="enterPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    rightElement={
                      <IconEyePasswordConfirm
                        className="cursor-pointer"
                        onClick={() => {
                          this.setState({ showConfirmPassword: !showConfirmPassword });
                        }}
                      />
                    }
                  />
                  <InputError error={errors.confirm_password} />
                </div>
              </Col>
            </Row>
          </div>

          <div
            style={{ marginBottom: 'calc(var(--sp-md2)*1.79245)', marginTop: 'calc(var(--sp-lg)*2.35849)' }}
            className="flex-center-all max-width-100"
          >
            <ButtonGradient
              onClick={this.handleSubmit}
              textStyle={{ fontWeight: 700 }}
              title="save"
              loading={submitting}
            />
          </div>
        </div>
      </motion.div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    userInfo: getValueFromObjectByKeys(state, ['User', 'userInfo'])
  };
};

const mapDispatchToProps = () => {
  return {};
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(WithNavigate(withTokenParam(SetPassword)));
